import jsPDF from "jspdf";
import "jspdf-autotable";

// Extend jsPDF type to include autoTable
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

export interface ReceiptData {
  id: string;
  type: "transaction" | "withdrawal";
  amount: number;
  currency: string;
  status: string;
  date: string;
  description?: string;
  milestone?: {
    title: string;
    description?: string;
  };
  contract?: {
    id: string;
    title: string;
  };
  user?: {
    name: string;
    email: string;
  };
  platformFee?: number;
  emergencyCost?: number;
  trackerAmount?: number;
  withdrawlAmount?: number;
  totalAmount?: number;
  paymentMethod?: string;
  paymentId?: string;
  expectedReleaseDate?: string;
  actualReleaseDate?: string;
  disputeDescription?: string;
  notes?: string;
}

export class ReceiptGenerator {
  private doc: jsPDF;

  constructor() {
    this.doc = new jsPDF();
    
    // Check if autoTable is available
    if (!(this.doc as any).autoTable) {
      console.warn("jsPDF autoTable plugin not loaded. Tables will not be rendered.");
    }
  }

  private addHeader(companyName: string = "Kokobeo", companyAddress: string = "123 Business St, City, State 12345") {
    // Company Logo/Name
    this.doc.setFontSize(20);
    this.doc.setFont("helvetica", "bold");
    this.doc.text(companyName, 20, 30);

    // Company Address
    this.doc.setFontSize(10);
    this.doc.setFont("helvetica", "normal");
    this.doc.text(companyAddress, 20, 40);
    this.doc.text("Email: support@kokobeo.com", 20, 50);
    this.doc.text("Phone: +1 (555) 123-4567", 20, 60);

    // Receipt Title
    this.doc.setFontSize(16);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("RECEIPT", 150, 30);

    // Date
    this.doc.setFontSize(10);
    this.doc.setFont("helvetica", "normal");
    this.doc.text(`Generated: ${new Date().toLocaleDateString()}`, 150, 40);
  }

  private addTransactionDetails(data: ReceiptData) {
    const startY = 80;

    // Transaction ID
    this.doc.setFontSize(12);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Transaction Details", 20, startY);

    this.doc.setFontSize(10);
    this.doc.setFont("helvetica", "normal");
    this.doc.text(`Transaction ID: ${data.id}`, 20, startY + 10);
    this.doc.text(`Type: ${data.type === "transaction" ? "Escrow Payment" : "Withdrawal"}`, 20, startY + 20);
    this.doc.text(`Status: ${data.status}`, 20, startY + 30);
    this.doc.text(`Date: ${new Date(data.date).toLocaleString()}`, 20, startY + 40);

    if (data.description || data.milestone?.title) {
      this.doc.text(`Description: ${data.milestone?.title || data.description || "N/A"}`, 20, startY + 50);
    }

    if (data.contract?.title) {
      this.doc.text(`Contract: ${data.contract.title}`, 20, startY + 60);
    }

    if (data.user?.name) {
      this.doc.text(`User: ${data.user.name}`, 20, startY + 70);
    }

    if (data.paymentMethod) {
      this.doc.text(`Payment Method: ${data.paymentMethod}`, 20, startY + 80);
    }

    if (data.paymentId) {
      this.doc.text(`Payment ID: ${data.paymentId}`, 20, startY + 90);
    }

    return startY + 100;
  }

  private addAmountBreakdown(data: ReceiptData, startY: number) {
    this.doc.setFontSize(12);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Amount Breakdown", 20, startY);

    const breakdownData = [];
    
    if (data.withdrawlAmount) {
      breakdownData.push(["Base Amount", `${data.currency} ${data.withdrawlAmount.toFixed(2)}`]);
    }

    if (data.emergencyCost && data.emergencyCost > 0) {
      breakdownData.push(["Emergency Cost", `${data.currency} ${data.emergencyCost.toFixed(2)}`]);
    }

    if (data.trackerAmount && data.trackerAmount > 0) {
      breakdownData.push(["Tracker Amount", `${data.currency} ${data.trackerAmount.toFixed(2)}`]);
    }

    if (data.platformFee && data.platformFee > 0) {
      breakdownData.push(["Platform Fee", `${data.currency} ${data.platformFee.toFixed(2)}`]);
    }

    breakdownData.push(["Total Amount", `${data.currency} ${(data.totalAmount || data.amount).toFixed(2)}`]);

    if ((this.doc as any).autoTable) {
      (this.doc as any).autoTable({
        startY: startY + 10,
        head: [["Item", "Amount"]],
        body: breakdownData,
        theme: "grid",
        headStyles: {
          fillColor: [59, 130, 246], // Blue color
          textColor: 255,
          fontStyle: "bold"
        },
        styles: {
          fontSize: 10,
          cellPadding: 5
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 60, halign: "right" }
        }
      });
      return (this.doc as any).lastAutoTable.finalY + 10;
    } else {
      // Fallback: render table as text
      let currentY = startY + 20;
      this.doc.setFontSize(10);
      this.doc.setFont("helvetica", "bold");
      this.doc.text("Item", 20, currentY);
      this.doc.text("Amount", 120, currentY);
      currentY += 10;
      
      this.doc.setFont("helvetica", "normal");
      breakdownData.forEach((row) => {
        this.doc.text(row[0], 20, currentY);
        this.doc.text(row[1], 120, currentY);
        currentY += 8;
      });
      return currentY + 10;
    }
  }

  private addTimeline(data: ReceiptData, startY: number) {
    this.doc.setFontSize(12);
    this.doc.setFont("helvetica", "bold");
    this.doc.text("Timeline", 20, startY);

    const timelineData = [];
    
    timelineData.push(["Created", new Date(data.date).toLocaleString()]);

    if (data.expectedReleaseDate) {
      timelineData.push(["Expected Release", new Date(data.expectedReleaseDate).toLocaleString()]);
    }

    if (data.actualReleaseDate) {
      timelineData.push(["Released", new Date(data.actualReleaseDate).toLocaleString()]);
    }

    if ((this.doc as any).autoTable) {
      (this.doc as any).autoTable({
        startY: startY + 10,
        head: [["Event", "Date"]],
        body: timelineData,
        theme: "grid",
        headStyles: {
          fillColor: [34, 197, 94], // Green color
          textColor: 255,
          fontStyle: "bold"
        },
        styles: {
          fontSize: 10,
          cellPadding: 5
        },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 100 }
        }
      });
      return (this.doc as any).lastAutoTable.finalY + 10;
    } else {
      // Fallback: render table as text
      let currentY = startY + 20;
      this.doc.setFontSize(10);
      this.doc.setFont("helvetica", "bold");
      this.doc.text("Event", 20, currentY);
      this.doc.text("Date", 120, currentY);
      currentY += 10;
      
      this.doc.setFont("helvetica", "normal");
      timelineData.forEach((row) => {
        this.doc.text(row[0], 20, currentY);
        this.doc.text(row[1], 120, currentY);
        currentY += 8;
      });
      return currentY + 10;
    }
  }

  private addFooter(startY: number) {
    const pageHeight = this.doc.internal.pageSize.height;
    const footerY = Math.max(startY + 20, pageHeight - 40);

    // Footer line
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(20, footerY, 190, footerY);

    // Footer text
    this.doc.setFontSize(8);
    this.doc.setFont("helvetica", "normal");
    this.doc.text("Thank you for using Kokobeo!", 20, footerY + 10);
    this.doc.text("For support, contact us at support@kokobeo.com", 20, footerY + 20);
    this.doc.text("This is an automated receipt generated by the system.", 20, footerY + 30);
  }

  private addDisputeInfo(data: ReceiptData, startY: number) {
    if (data.disputeDescription) {
      this.doc.setFontSize(12);
      this.doc.setFont("helvetica", "bold");
      this.doc.text("Dispute Information", 20, startY);

      this.doc.setFontSize(10);
      this.doc.setFont("helvetica", "normal");
      this.doc.text(data.disputeDescription, 20, startY + 10);

      return startY + 30;
    }
    return startY;
  }

  public generateTransactionReceipt(data: ReceiptData): void {
    this.doc = new jsPDF();
    
    // Add header
    this.addHeader();

    // Add transaction details
    let currentY = this.addTransactionDetails(data);

    // Add amount breakdown
    currentY = this.addAmountBreakdown(data, currentY);

    // Add timeline
    currentY = this.addTimeline(data, currentY);

    // Add dispute info if applicable
    currentY = this.addDisputeInfo(data, currentY);

    // Add footer
    this.addFooter(currentY);

    // Generate filename
    const filename = `receipt_${data.type}_${data.id}_${new Date().toISOString().split("T")[0]}.pdf`;

    // Download the PDF
    this.doc.save(filename);
  }

  public generateWithdrawalReceipt(data: ReceiptData): void {
    this.doc = new jsPDF();
    
    // Add header
    this.addHeader();

    // Add transaction details
    let currentY = this.addTransactionDetails(data);

    // Add amount breakdown
    currentY = this.addAmountBreakdown(data, currentY);

    // Add timeline
    currentY = this.addTimeline(data, currentY);

    // Add dispute info if applicable
    currentY = this.addDisputeInfo(data, currentY);

    // Add footer
    this.addFooter(currentY);

    // Generate filename
    const filename = `withdrawal_receipt_${data.id}_${new Date().toISOString().split("T")[0]}.pdf`;

    // Download the PDF
    this.doc.save(filename);
  }

  public generateReceipt(data: ReceiptData): void {
    try {
      if (!data || !data.id) {
        throw new Error("Invalid receipt data provided");
      }
      
      if (data.type === "withdrawal") {
        this.generateWithdrawalReceipt(data);
      } else {
        this.generateTransactionReceipt(data);
      }
    } catch (error) {
      console.error("Error generating receipt:", error);
      throw error;
    }
  }
}

// Utility function to create receipt data from transaction
export const createTransactionReceiptData = (transaction: any): ReceiptData => {
  if (!transaction) {
    throw new Error("Transaction data is required");
  }

  return {
    id: transaction.id || "unknown",
    type: "transaction",
    amount: transaction.totalAmount || transaction.amount || 0,
    currency: transaction.currency || "USD",
    status: transaction.status?.name || "Unknown",
    date: transaction.createdAt || new Date().toISOString(),
    description: transaction.notes,
    milestone: transaction.milestone ? {
      title: transaction.milestone.title || "Milestone",
      description: transaction.milestone.description
    } : undefined,
    contract: transaction.contract ? {
      id: transaction.contract.id || "unknown",
      title: transaction.contract.title || "Contract"
    } : undefined,
    user: transaction.user ? {
      name: transaction.user.name || (transaction.user.firstName && transaction.user.lastName ? 
        transaction.user.firstName + " " + transaction.user.lastName : "Unknown User"),
      email: transaction.user.email || "unknown@example.com"
    } : undefined,
    platformFee: transaction.platformFee || 0,
    emergencyCost: transaction.emergencyCost || 0,
    trackerAmount: transaction.trackerAmount || 0,
    withdrawlAmount: transaction.withdrawlAmount || 0,
    totalAmount: transaction.totalAmount || transaction.amount || 0,
    paymentMethod: transaction.paymentProvider === "stripe" ? "Stripe" : "Payment Gateway",
    paymentId: transaction.stripePaymentIntentId,
    expectedReleaseDate: transaction.expectedReleaseDate,
    actualReleaseDate: transaction.actualReleaseDate,
    disputeDescription: transaction.disputeDescription,
    notes: transaction.notes
  };
};

// Utility function to create receipt data from withdrawal
export const createWithdrawalReceiptData = (withdrawal: any): ReceiptData => {
  if (!withdrawal) {
    throw new Error("Withdrawal data is required");
  }

  // Map status number to label
  const getStatusLabel = (status: number) => {
    switch (status) {
      case 1: return "Pending";
      case 2: return "Processing";
      case 3: return "Completed";
      case 4: return "Failed";
      case 5: return "Cancelled";
      default: return "Unknown";
    }
  };

  // Determine payment method details
  let paymentMethod = "Bank Transfer";
  let paymentId = undefined;
  
  if (withdrawal.withdrawalMethod === "paypal") {
    paymentMethod = `PayPal (${withdrawal.paypalEmail || "N/A"})`;
    paymentId = withdrawal.paypalPayoutBatchId || withdrawal.paypalPayoutItemId;
  } else if (withdrawal.withdrawalMethod === "stripe") {
    paymentMethod = "Stripe Connect";
    paymentId = withdrawal.stripeTransferId || withdrawal.stripePayoutId;
  }

  return {
    id: withdrawal.id || "unknown",
    type: "withdrawal",
    amount: withdrawal.amount || 0,
    currency: (withdrawal.currency || "USD").toUpperCase(),
    status: getStatusLabel(withdrawal.status),
    date: withdrawal.createdAt || withdrawal.date || new Date().toISOString(),
    description: `Withdrawal via ${withdrawal.withdrawalMethod || "Stripe"}`,
    user: withdrawal.user ? {
      name: withdrawal.user.name || (withdrawal.user.firstName && withdrawal.user.lastName ? 
        withdrawal.user.firstName + " " + withdrawal.user.lastName : "Unknown User"),
      email: withdrawal.user.email || "unknown@example.com"
    } : undefined,
    platformFee: 0,
    withdrawlAmount: withdrawal.amount || 0,
    totalAmount: withdrawal.amount || 0,
    paymentMethod: paymentMethod,
    paymentId: paymentId,
    expectedReleaseDate: undefined,
    actualReleaseDate: withdrawal.completedAt,
    notes: withdrawal.notes
  };
};
